import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Eye, MousePointerClick, PhoneCall, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/components/marketplace/shared";
import { FeaturePurchaseSheet } from "@/components/featured/FeaturePurchaseSheet";
import { FEATURE_PURCHASE_ALLOWED } from "@/components/featured/FeatureButton";

export const FEATURED_STATUS = {
  pending_payment: ["Pagamento pendente", "bg-yellow-100 text-yellow-800"],
  active: ["Ativo", "bg-emerald-100 text-emerald-700"],
  expired: ["Terminado", "bg-zinc-100 text-zinc-600"],
  cancelled: ["Cancelado", "bg-red-100 text-red-700"],
  refunded: ["Reembolsado", "bg-orange-100 text-orange-700"],
};
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export function StatusPill({ status, testid }) {
  const [label, cls] = FEATURED_STATUS[status] || [status, "bg-zinc-100 text-zinc-600"];
  return <span data-testid={testid} className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${cls}`}>{label}</span>;
}

export default function MyFeatured() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [sheet, setSheet] = useState(null);

  const load = () => api.get("/featured/mine").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => { if (sp.get("cancelled")) toast("Pagamento cancelado. Nenhum destaque foi ativado."); }, [sp]);

  return (
    <div className="page fade-up" data-testid="my-featured-page">
      <Link to="/perfil" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Perfil</Link>
      <h1 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2"><Sparkles size={20} /> Os meus destaques</h1>
      <p className="text-xs text-zinc-500 mb-4">Períodos pagos de maior visibilidade. O destaque não garante vendas, contactos ou visualizações.</p>

      {items === null && <p className="text-sm text-zinc-500 text-center py-8">A carregar…</p>}
      {items && items.length === 0 && (
        <div data-testid="my-featured-empty" className="text-center py-10 text-zinc-500 text-sm bg-white rounded-2xl border border-zinc-200 px-4">
          Ainda não destacaste nenhum conteúdo.<br />Abre um produto, serviço, negócio, classificado ou evento teu e toca em <span className="font-semibold text-black">Destacar</span>.
        </div>
      )}
      <div className="space-y-3">
        {items?.map((f) => <FeaturedCard key={f.id} f={f} onAction={() => setSheet(f)} />)}
      </div>
      {sheet && (
        <FeaturePurchaseSheet open onClose={() => { setSheet(null); load(); }} targetType={sheet.featured_target_type} targetId={sheet.featured_target_id}
                              targetName={sheet.target_name} active={sheet.featured_status === "active" ? sheet : null} />
      )}
    </div>
  );
}

function FeaturedCard({ f, onAction }) {
  const paid = f.featured_amount_minor || 0;
  const m = f.metrics || {};
  const active = f.featured_status === "active";
  return (
    <div data-testid={`my-featured-${f.id}`} className="bg-white rounded-2xl border border-zinc-200 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{f.type_label}</div>
          <Link to={f.path} className="font-bold text-sm truncate block hover:underline">{f.target_name || "Conteúdo"} <ExternalLink size={10} className="inline text-zinc-400" /></Link>
        </div>
        <StatusPill status={f.featured_status} testid={`my-featured-status-${f.id}`} />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        <Cell k="Início" v={fmtDate(f.featured_started_at)} />
        <Cell k="Fim" v={fmtDate(f.featured_ends_at)} />
        <Cell k={active ? "Dias restantes" : "Valor pago"} v={active ? <span data-testid={`my-featured-days-${f.id}`}>{f.days_remaining}</span> : money(paid, "GBP")} />
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-zinc-600" data-testid={`my-featured-metrics-${f.id}`}>
        <span className="flex items-center gap-1"><Eye size={12} /> {m.impression || 0} impressões</span>
        <span className="flex items-center gap-1"><MousePointerClick size={12} /> {m.click || 0} cliques</span>
        {(m.cta || 0) > 0 && <span className="flex items-center gap-1"><PhoneCall size={12} /> {m.cta} contactos</span>}
        {active && <span className="ml-auto text-zinc-400">{money(paid, "GBP")} pagos</span>}
      </div>
      {f.featured_status !== "pending_payment" && FEATURE_PURCHASE_ALLOWED && (
        <button data-testid={`my-featured-action-${f.id}`} onClick={onAction}
                className="mt-3 w-full py-2.5 rounded-xl border border-black text-xs font-bold hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1">
          <Sparkles size={12} /> {active ? "Prolongar destaque" : "Destacar novamente"}
        </button>
      )}
      {!active && f.featured_status === "expired" && <p className="text-[11px] text-zinc-500 mt-2 text-center">Destaque terminado — o conteúdo continua publicado normalmente.</p>}
    </div>
  );
}

function Cell({ k, v }) {
  return <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-2"><div className="text-[10px] text-zinc-500">{k}</div><div className="font-semibold">{v}</div></div>;
}
